import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  ACTIVITY_TIMER_TAB_ID,
  loadActivityTimerSnapshotRecord,
  saveActivityTimerSnapshot,
  subscribeActivityTimerSnapshot,
} from '../../../services/activityTimerPersistence'
import {
  selectActivityTimerRuntime,
  TIMER_ACTION,
  TIMER_STATUS,
} from '../model/activityTimerSlice'

export default function ActivityTimerManager({ children, tabId = ACTIVITY_TIMER_TAB_ID }) {
  const dispatch = useDispatch()
  const { timer, ownerId } = useSelector(selectActivityTimerRuntime)
  const timerRef = useRef(timer)
  const ownerIdRef = useRef(ownerId)

  timerRef.current = timer
  ownerIdRef.current = ownerId

  useEffect(() => {
    const restoredRecord = loadActivityTimerSnapshotRecord()
    dispatch({
      type: TIMER_ACTION.INITIALIZE,
      state: restoredRecord?.state,
      ownerId: restoredRecord?.ownerId,
      tabId,
    })
    return () => {
      dispatch({ type: TIMER_ACTION.INITIALIZE, state: null, ownerId: null, tabId })
    }
  }, [dispatch, tabId])

  useEffect(() => {
    if (timer.status !== TIMER_STATUS.IDLE && ownerId === tabId) {
      saveActivityTimerSnapshot(timer, undefined, { ownerId: tabId })
    }
  }, [ownerId, tabId, timer])

  useEffect(() => subscribeActivityTimerSnapshot((record) => {
    if (!record) {
      if (ownerIdRef.current !== tabId) {
        dispatch({ type: TIMER_ACTION.SYNC_EXTERNAL, state: null, ownerId: null })
      }
      return
    }
    if (record.ownerId === tabId) return
    dispatch({
      type: TIMER_ACTION.SYNC_EXTERNAL,
      state: record.state,
      ownerId: record.ownerId,
    })
  }), [dispatch, tabId])

  useEffect(() => {
    if (timer.status === TIMER_STATUS.IDLE || ownerId !== tabId) return undefined
    const heartbeatId = window.setInterval(() => {
      saveActivityTimerSnapshot(timerRef.current, undefined, { ownerId: tabId })
    }, 5000)
    return () => window.clearInterval(heartbeatId)
  }, [ownerId, tabId, timer.status])

  useEffect(() => {
    const hasUnfinishedTimer = ![TIMER_STATUS.IDLE, TIMER_STATUS.PREPARING]
      .includes(timer.status)
    if (!hasUnfinishedTimer) return undefined

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [timer.status])

  return children
}
